import { useState } from "react";
import DocAttachment from "@/common/DocAttachment";
import { getAttachmentMediaType } from "%/mediaType";
import { useAttachmentThumbnail } from "^/business/notify/useAttachmentThumbnail";

interface AttachmentPreviewProps {
  fileSeq: number;
  originalUrl: string;
  thumbnailUrl?: string | null;
  thumbnailStatus?: string | null;
}

const labels = {
  ready: "",
  loading: "썸네일 불러오는 중",
  pending: "썸네일 생성 중",
  delayed: "썸네일 생성 지연",
  failed: "썸네일 생성 실패",
  unavailable: "썸네일 없음",
  error: "썸네일 조회 실패",
};

const AttachmentPreview = (props: AttachmentPreviewProps) => {
  const mediaType = getAttachmentMediaType(props.originalUrl);
  const { thumbnailUrl, state, retry, isFetching, canFetch } =
    useAttachmentThumbnail(props.fileSeq, props, mediaType !== "document");
  const [failedUrl, setFailedUrl] = useState<string>();
  const [imageAttempt, setImageAttempt] = useState(0);

  if (mediaType === "document") return <DocAttachment url={props.originalUrl} />;

  const imageFailed = !!thumbnailUrl && failedUrl === thumbnailUrl;
  const showThumbnail = !!thumbnailUrl && !imageFailed;
  const label = imageFailed ? "썸네일 표시 실패" : labels[state];
  const canRetry = canFetch && (
    imageFailed || ["delayed", "failed", "unavailable", "error"].includes(state)
  );

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-gray-100">
      {showThumbnail ? (
        <img
          key={`${thumbnailUrl}-${imageAttempt}`}
          src={thumbnailUrl}
          alt={`첨부파일 ${props.fileSeq} 썸네일`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setFailedUrl(thumbnailUrl)}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center text-[11px] text-gray-500">
          <span>{label}</span>
          {canRetry && (
            <button
              type="button"
              className="px-2 py-1 underline disabled:opacity-50"
              disabled={isFetching}
              onClick={(event) => {
                event.stopPropagation();
                setFailedUrl(undefined);
                setImageAttempt((attempt) => attempt + 1);
                retry();
              }}
            >
              다시 불러오기
            </button>
          )}
        </div>
      )}
      {mediaType === "video" && (
        <div className={showThumbnail
          ? "pointer-events-none absolute inset-0 flex items-center justify-center bg-black bg-opacity-20"
          : "pointer-events-none absolute bottom-1 right-1 text-gray-400"
        }>
          <svg
            className={showThumbnail ? "h-8 w-8 text-white opacity-80" : "h-4 w-4"}
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-label="동영상"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AttachmentPreview;
